import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { PageResponse } from '@models/api';
import { getOrders, GetOrdersParams, OrderResponse } from '@apis/order';
import { dropEmptyValues } from '../../utils/common';
import { User, UserGroup } from '@models/user';

const PAGE_SIZE = 50;

/**
 * Orders list query.
 *
 * Notes:
 * - React Query cache must be keyed by *all* inputs that affect the result set.
 *   (page + user scope + applied search filters)
 * - Partner users are always scoped to their own partnerId for safety.
 */
export const useOrderList = ({
  page,
  searchParam,
  user,
}: {
  page: number;
  searchParam: GetOrdersParams;
  user: User | null;
}) => {
  const isPartner = user?.group === UserGroup.PARTNER;

  /**
   * Normalize request params:
   * - Remove empty values for stable caching and consistent requests.
   * - Force partnerId for Partner users (server-side safety + consistent UI).
   */
  const normalizedSearch: GetOrdersParams = {
    ...dropEmptyValues(searchParam, [
      'orderCode',
      'productCode',
      'productName',
      'stockId',
      'partnerId',
      'orderStatus',
      'createdAtFrom',
      'createdAtTo',
      'reservedAtFrom',
      'reservedAtTo',
      'iccid',
      'passportNumber',
      'requestOrderId',
      'externalOrderId',
      'productSaleType',
    ]),
    ...(isPartner && user?.partnerId ? { partnerId: user.partnerId } : {}),
  };

  const queryOptions: UseQueryOptions<PageResponse<OrderResponse>, Error> = {
    /**
     * Cache key must include every factor that changes the response.
     * Otherwise the UI can show stale data until a hard refresh.
     */
    queryKey: ['orders', page, user?.userId, isPartner ? user?.partnerId : null, normalizedSearch],

    queryFn: () =>
      getOrders({
        pageNo: page,
        pageSize: PAGE_SIZE,
        ...normalizedSearch,
      }),

    enabled: Boolean(user) && (isPartner ? Boolean(user?.partnerId) : true),

    // Optional: usually safe to keep defaults; leaving as-is.
    refetchOnMount: true,
  };

  return useQuery(queryOptions);
};
